import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Users, Phone, Mail, BarChart3, LogOut, FileText, Download, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { adminLogin, adminGet, adminDownloadDocument } from "../lib/api";

const DOC_TYPE_LABEL = { pan: "PAN Card", aadhaar: "Aadhaar Card", income_proof: "Income Proof", bank_statement: "Bank Statement", address_proof: "Address Proof", other: "Other" };
const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("ol_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [busyDoc, setBusyDoc] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem("ol_admin_token");
    setToken(""); setPassword("");
  }, []);

  const loadAll = useCallback(async (t) => {
    try {
      const [s,l,cb,co,docs] = await Promise.all([
        adminGet("stats", t),
        adminGet("leads", t),
        adminGet("callbacks", t),
        adminGet("contacts", t),
        adminGet("documents", t),
      ]);
      setStats(s); setLeads(l); setCallbacks(cb); setContacts(co); setDocuments(docs);
    } catch {
      toast.error("Session expired");
      logout();
    }
  }, [logout]);

  useEffect(() => { if (token) loadAll(token); }, [token, loadAll]);

  const openDoc = useCallback(async (doc, save) => {
    setBusyDoc(doc.id);
    try {
      const blob = await adminDownloadDocument(doc.id, token);
      const url = URL.createObjectURL(blob);
      if (save) {
        const a = document.createElement("a");
        a.href = url; a.download = doc.original_filename || "document";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Download started");
      } else {
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch {
      toast.error("Could not fetch document");
    } finally {
      setBusyDoc(null);
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem("ol_admin_token", res.token);
      setToken(res.token);
      toast.success("Welcome back");
    } catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-white border border-[#E2E8F0] rounded-2xl p-8">
          <div className="w-12 h-12 rounded-lg bg-[#EAF5FD] flex items-center justify-center mx-auto"><Lock className="text-[#1088D4]"/></div>
          <h1 className="mt-5 text-2xl font-bold font-heading text-[#273B59] text-center">Admin Sign-in</h1>
          <p className="mt-1 text-xs text-[#475569] text-center">Enter the admin password to view leads.</p>
          <Input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-6"/>
          <Button data-testid="admin-login-btn" disabled={loading} className="w-full mt-4 ol-btn-primary h-11">
            {loading && <Loader2 className="animate-spin mr-2" size={14}/>}
            Sign In
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="ol-container py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading text-[#273B59]">Admin Dashboard</h1>
          <p className="text-sm text-[#475569] mt-1">Manage leads, callbacks and contact submissions.</p>
        </div>
        <Button data-testid="admin-logout" variant="outline" onClick={logout}><LogOut size={14} className="mr-2"/> Logout</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Total Leads" value={stats.leads_total}/>
          <StatCard icon={BarChart3} label="Today's Leads" value={stats.leads_today} accent/>
          <StatCard icon={Phone} label="Callbacks" value={stats.callbacks_total}/>
          <StatCard icon={Mail} label="Contact Forms" value={stats.contacts_total}/>
          <StatCard icon={FileText} label="Documents" value={stats.documents_total ?? documents.length}/>
        </div>
      )}

      <Tabs defaultValue="leads">
        <TabsList className="bg-[#F7F9FC]">
          <TabsTrigger data-testid="tab-leads" value="leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-callbacks" value="callbacks">Callbacks ({callbacks.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-contacts" value="contacts">Contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-documents" value="documents">Documents ({documents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6">
          <Table cols={["Date","Name","Mobile","Loan Type","Amount","Income","City","Lender","Source"]}>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-[#E2E8F0]">
                <td className="px-3 py-3 text-xs text-[#475569]">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="px-3 py-3 text-sm font-medium text-[#273B59]">{l.full_name || "—"}</td>
                <td className="px-3 py-3 text-sm">{l.mobile}</td>
                <td className="px-3 py-3"><Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0 capitalize">{l.loan_type}</Badge></td>
                <td className="px-3 py-3 text-sm">{l.loan_amount ? `₹${Number(l.loan_amount).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-3 py-3 text-sm">{l.monthly_income ? `₹${Number(l.monthly_income).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-3 py-3 text-sm">{l.city || "—"}</td>
                <td className="px-3 py-3 text-xs text-[#475569]">{l.lender || "—"}</td>
                <td className="px-3 py-3 text-xs text-[#475569]">{l.source}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="callbacks" className="mt-6">
          <Table cols={["Date","Name","Mobile","Email","Preferred Time","Message"]}>
            {callbacks.map((c) => (
              <tr key={c.id} className="border-t border-[#E2E8F0]">
                <td className="px-3 py-3 text-xs">{new Date(c.created_at).toLocaleString()}</td>
                <td className="px-3 py-3 text-sm font-medium text-[#273B59]">{c.full_name}</td>
                <td className="px-3 py-3 text-sm">{c.mobile}</td>
                <td className="px-3 py-3 text-sm">{c.email || "—"}</td>
                <td className="px-3 py-3 text-sm">{c.preferred_time || "—"}</td>
                <td className="px-3 py-3 text-xs max-w-xs truncate">{c.message || "—"}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>

        <TabsContent value="contacts" className="mt-6">
          <Table cols={["Date","Name","Email","Mobile","Subject","Message"]}>
            {contacts.map((c) => (
              <tr key={c.id} className="border-t border-[#E2E8F0]">
                <td className="px-3 py-3 text-xs">{new Date(c.created_at).toLocaleString()}</td>
                <td className="px-3 py-3 text-sm font-medium text-[#273B59]">{c.full_name}</td>
                <td className="px-3 py-3 text-sm">{c.email}</td>
                <td className="px-3 py-3 text-sm">{c.mobile || "—"}</td>
                <td className="px-3 py-3 text-sm">{c.subject || "—"}</td>
                <td className="px-3 py-3 text-xs max-w-xs truncate">{c.message}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="documents" className="mt-6">
          <Table cols={["Date","User","Email","Type","File Name","Size","Actions"]}>
            {documents.map((d) => (
              <tr key={d.id} data-testid={`admin-doc-${d.id}`} className="border-t border-[#E2E8F0]">
                <td className="px-3 py-3 text-xs text-[#475569]">{new Date(d.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="px-3 py-3 text-sm font-medium text-[#273B59]">{d.user_name || "—"}</td>
                <td className="px-3 py-3 text-sm">{d.user_email || "—"}</td>
                <td className="px-3 py-3"><Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0">{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</Badge></td>
                <td className="px-3 py-3 text-sm max-w-[200px] truncate">{d.original_filename}</td>
                <td className="px-3 py-3 text-sm">{fmtBytes(d.size)}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <Button data-testid={`admin-doc-view-${d.id}`} size="icon" variant="ghost" disabled={busyDoc === d.id} onClick={() => openDoc(d, false)} className="text-[#1088D4] h-8 w-8"><Eye size={15}/></Button>
                    <Button data-testid={`admin-doc-download-${d.id}`} size="icon" variant="ghost" disabled={busyDoc === d.id} onClick={() => openDoc(d, true)} className="text-[#273B59] h-8 w-8">{busyDoc === d.id ? <Loader2 size={15} className="animate-spin"/> : <Download size={15}/>}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
          {documents.length === 0 && <p data-testid="admin-docs-empty" className="text-center text-sm text-[#94A3B8] py-8">No documents uploaded by any user yet.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl p-5 border ${accent ? "bg-[#1088D4] text-white border-[#1088D4]" : "bg-white border-[#E2E8F0] text-[#273B59]"}`}>
    <Icon size={20} className={accent ? "text-white/90" : "text-[#1088D4]"}/>
    <div className={`mt-3 text-xs uppercase tracking-wider ${accent ? "text-white/80" : "text-[#94A3B8]"}`}>{label}</div>
    <div className="mt-1 text-3xl font-bold font-heading">{value}</div>
  </div>
);

const Table = ({ cols, children }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
    <table className="w-full text-left">
      <thead className="bg-[#F7F9FC]">
        <tr>{cols.map(c => <th key={c} className="px-3 py-3 text-[10px] uppercase tracking-wider text-[#475569]">{c}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
